import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Eye, EyeOff, Copy, Plus, Pencil, Trash2, KeyRound, ExternalLink, History, Building2, Search,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = { label: "", username: "", password: "", url: "", notes: "", sucursal: "" };

const CredentialsPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sucursalFilter, setSucursalFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // credential doc being edited, or null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({}); // { [credId]: "plain password" }
  const [revealing, setRevealing] = useState({});
  const [logOpen, setLogOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logTitle, setLogTitle] = useState("");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/credentials`, authHeaders());
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar contraseñas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sucursales = Array.from(new Set(items.map(i => i.sucursal).filter(Boolean))).sort();

  const filtered = items.filter(i => {
    if (sucursalFilter && i.sucursal !== sucursalFilter) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [i.label, i.username, i.url, i.notes, i.sucursal]
      .some(v => (v || "").toLowerCase().includes(s));
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };

  const openEdit = (cred) => {
    setEditing(cred);
    setForm({
      label: cred.label || "",
      username: cred.username || "",
      password: "", // blank = keep existing
      url: cred.url || "",
      notes: cred.notes || "",
      sucursal: cred.sucursal || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      toast.error("La etiqueta es obligatoria");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        // If password field left blank on edit → don't send it (keeps existing)
        if (!payload.password) delete payload.password;
        await axios.patch(`${API}/credentials/${editing.id}`, payload, authHeaders());
        toast.success("Credencial actualizada");
      } else {
        await axios.post(`${API}/credentials`, form, authHeaders());
        toast.success("Credencial creada");
      }
      setModalOpen(false);
      setForm(EMPTY);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cred) => {
    if (!window.confirm(`¿Eliminar "${cred.label}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/credentials/${cred.id}`, authHeaders());
      toast.success("Eliminada");
      setRevealed(prev => { const c = {...prev}; delete c[cred.id]; return c; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleReveal = async (cred) => {
    if (revealed[cred.id] !== undefined) {
      // Hide
      setRevealed(prev => { const c = {...prev}; delete c[cred.id]; return c; });
      return;
    }
    if (!cred.has_password) {
      toast.info("Esta entrada no tiene contraseña guardada");
      return;
    }
    setRevealing(prev => ({...prev, [cred.id]: true}));
    try {
      const res = await axios.post(`${API}/credentials/${cred.id}/reveal`, null, authHeaders());
      setRevealed(prev => ({...prev, [cred.id]: res.data.password}));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al descifrar");
    } finally {
      setRevealing(prev => ({...prev, [cred.id]: false}));
    }
  };

  const handleCopy = async (cred, field) => {
    let value = cred[field];
    if (field === "password") {
      // If already revealed, use cached. Otherwise fetch (also logs).
      if (revealed[cred.id] !== undefined) {
        value = revealed[cred.id];
      } else if (cred.has_password) {
        try {
          const res = await axios.post(`${API}/credentials/${cred.id}/reveal`, null, authHeaders());
          value = res.data.password;
          setRevealed(prev => ({...prev, [cred.id]: value}));
        } catch (e) {
          toast.error("No se pudo copiar la contraseña");
          return;
        }
      } else {
        toast.info("Sin contraseña guardada");
        return;
      }
    }
    if (!value) {
      toast.info("Vacío, no hay nada que copiar");
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${field === "password" ? "Contraseña" : "Usuario"} copiado`);
    } catch {
      toast.error("Tu navegador bloqueó el portapapeles");
    }
  };

  const openLog = async (cred) => {
    setLogTitle(cred.label);
    setLogs([]);
    setLogOpen(true);
    try {
      const res = await axios.get(`${API}/credentials/${cred.id}/access-log`, authHeaders());
      setLogs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar historial");
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="credentials-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
              <KeyRound className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Contraseñas</h1>
              <p className="text-sm text-[#6B7280]">
                Guarda credenciales de POS, tablets, streaming, redes... cifradas en la base de datos.
              </p>
            </div>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#F43F5E] hover:bg-[#E11D48] text-white"
            data-testid="credentials-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nueva Credencial
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, usuario, notas..."
              className="pl-9 bg-white border-[#FECDD3]"
              data-testid="credentials-search-input"
            />
          </div>
          {sucursales.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <Button
                variant={sucursalFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSucursalFilter("")}
                className={sucursalFilter === "" ? "bg-[#8B5CF6] text-white hover:bg-[#7C3AED]" : ""}
                data-testid="sucursal-filter-all"
              >
                Todas
              </Button>
              {sucursales.map(s => (
                <Button
                  key={s}
                  variant={sucursalFilter === s ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSucursalFilter(s)}
                  className={sucursalFilter === s ? "bg-[#8B5CF6] text-white hover:bg-[#7C3AED]" : ""}
                  data-testid={`sucursal-filter-${s}`}
                >
                  <Building2 className="w-3 h-3 mr-1" /> {s}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-[#6B7280]">Cargando...</div>
        ) : filtered.length === 0 ? (
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="text-center py-16 text-[#6B7280]">
              {items.length === 0
                ? "Aún no hay credenciales guardadas. Dale a \"Nueva Credencial\" para arrancar."
                : "Ningún resultado con esos filtros."}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map(cred => {
              const isRevealed = revealed[cred.id] !== undefined;
              return (
                <Card key={cred.id} className="bg-white border-[#FECDD3] shadow-sm" data-testid={`credential-card-${cred.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-[#4A4A4A] font-['Manrope'] text-base flex items-center gap-2 flex-wrap">
                          <KeyRound className="w-4 h-4 text-[#8B5CF6] flex-shrink-0" />
                          <span className="break-words">{cred.label}</span>
                          {cred.sucursal && (
                            <Badge variant="outline" className="border-[#8B5CF6] text-[#8B5CF6] text-[10px]">
                              <Building2 className="w-3 h-3 mr-1" /> {cred.sucursal}
                            </Badge>
                          )}
                        </CardTitle>
                        {cred.url && (
                          <a href={cred.url.startsWith("http") ? cred.url : `https://${cred.url}`}
                             target="_blank" rel="noopener noreferrer"
                             className="text-xs text-[#8B5CF6] hover:underline inline-flex items-center gap-1 mt-1 break-all">
                            <ExternalLink className="w-3 h-3" /> {cred.url}
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="icon" variant="ghost" onClick={() => openLog(cred)} title="Historial" data-testid={`credential-log-${cred.id}`}>
                          <History className="w-4 h-4 text-[#6B7280]" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(cred)} title="Editar" data-testid={`credential-edit-${cred.id}`}>
                          <Pencil className="w-4 h-4 text-[#3B82F6]" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(cred)} title="Eliminar" data-testid={`credential-delete-${cred.id}`}>
                          <Trash2 className="w-4 h-4 text-[#EF4444]" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {cred.username && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#6B7280] w-20 flex-shrink-0">Usuario</span>
                        <code className="flex-1 bg-[#F9FAFB] px-2 py-1.5 rounded text-sm text-[#4A4A4A] font-mono break-all">
                          {cred.username}
                        </code>
                        <Button size="icon" variant="ghost" onClick={() => handleCopy(cred, "username")} title="Copiar usuario" data-testid={`credential-copy-user-${cred.id}`}>
                          <Copy className="w-4 h-4 text-[#6B7280]" />
                        </Button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#6B7280] w-20 flex-shrink-0">Contraseña</span>
                      <code className="flex-1 bg-[#F9FAFB] px-2 py-1.5 rounded text-sm text-[#4A4A4A] font-mono break-all min-h-[32px] flex items-center">
                        {!cred.has_password ? (
                          <span className="text-[#9CA3AF] italic">— vacía —</span>
                        ) : isRevealed ? (
                          revealed[cred.id]
                        ) : (
                          <span className="tracking-widest">••••••••••</span>
                        )}
                      </code>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleReveal(cred)}
                        disabled={revealing[cred.id]}
                        title={isRevealed ? "Ocultar" : "Mostrar"}
                        data-testid={`credential-reveal-${cred.id}`}
                      >
                        {isRevealed
                          ? <EyeOff className="w-4 h-4 text-[#F59E0B]" />
                          : <Eye className="w-4 h-4 text-[#6B7280]" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => handleCopy(cred, "password")} title="Copiar contraseña" data-testid={`credential-copy-pass-${cred.id}`}>
                        <Copy className="w-4 h-4 text-[#6B7280]" />
                      </Button>
                    </div>
                    {cred.notes && (
                      <div className="pt-2 border-t border-[#F3F4F6]">
                        <p className="text-xs text-[#6B7280] whitespace-pre-wrap">{cred.notes}</p>
                      </div>
                    )}
                    <div className="text-[10px] text-[#9CA3AF] pt-1">
                      Última actualización: {cred.updated_at ? new Date(cred.updated_at).toLocaleString("es-MX") : "—"}
                      {cred.updated_by_email && ` · por ${cred.updated_by_email}`}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#4A4A4A]">{editing ? "Editar Credencial" : "Nueva Credencial"}</DialogTitle>
            <DialogDescription className="text-[#6B7280]">
              La contraseña se guarda cifrada en la base de datos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="label">Etiqueta *</Label>
              <Input id="label" value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder="Ej. POS Sucursal Centro" data-testid="cred-form-label" />
            </div>
            <div>
              <Label htmlFor="username">Usuario</Label>
              <Input id="username" value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="usuario, email, PIN, etc." autoComplete="off" data-testid="cred-form-username" />
            </div>
            <div>
              <Label htmlFor="password">
                Contraseña {editing && <span className="text-[#9CA3AF] text-xs">(dejar en blanco para no cambiarla)</span>}
              </Label>
              <Input id="password" type="text" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editing ? "•••••••• (sin cambios)" : "cualquier texto o PIN"} autoComplete="off" data-testid="cred-form-password" />
            </div>
            <div>
              <Label htmlFor="url">URL (opcional)</Label>
              <Input id="url" value={form.url} onChange={e => setForm({...form, url: e.target.value})} placeholder="https://..." data-testid="cred-form-url" />
            </div>
            <div>
              <Label htmlFor="sucursal">Sucursal (opcional)</Label>
              <Input id="sucursal" value={form.sucursal} onChange={e => setForm({...form, sucursal: e.target.value})} placeholder="Ej. Centro, Merch Online" data-testid="cred-form-sucursal" />
            </div>
            <div>
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea id="notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Comentarios, política de expiración, etc." rows={3} data-testid="cred-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#F43F5E] hover:bg-[#E11D48] text-white" data-testid="cred-form-save">
              {saving ? "Guardando..." : editing ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access log modal */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#4A4A4A]">Historial · {logTitle}</DialogTitle>
            <DialogDescription className="text-[#6B7280]">Últimos 500 eventos.</DialogDescription>
          </DialogHeader>
          {logs.length === 0 ? (
            <div className="text-center text-[#6B7280] py-8 text-sm">Sin eventos aún.</div>
          ) : (
            <div className="space-y-1">
              {logs.map(l => (
                <div key={l.id} className="flex items-center justify-between gap-2 text-xs border-b border-[#F3F4F6] py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={
                      l.action === "view" ? "bg-[#E0F2FE] text-[#0C4A6E]"
                      : l.action === "create" ? "bg-[#D1FAE5] text-[#065F46]"
                      : l.action === "update" ? "bg-[#FEF3C7] text-[#92400E]"
                      : "bg-[#FEE2E2] text-[#991B1B]"
                    }>
                      {l.action}
                    </Badge>
                    <span className="text-[#4A4A4A] truncate">{l.user_email}</span>
                    {l.user_role && <span className="text-[#9CA3AF]">({l.user_role})</span>}
                  </div>
                  <span className="text-[#6B7280] flex-shrink-0">
                    {new Date(l.timestamp).toLocaleString("es-MX")}
                  </span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CredentialsPage;
